import { getFullName } from "encamp-shared/src/utils/name";
import { ReviewerStatus, SubmissionReviewerInput } from "generated-graphql/graphql";
import { useCallback } from "react";

type PersistedReviewer = {
  reviewerStatus?: ReviewerStatus | null;
  user: {
    id: string;
    email: string;
    person?: { first?: string | null; last?: string | null } | null;
  };
};

/**
 * Dropping a reviewer who already approved discards their review. Staff aren't
 * blocked from it — a configuration they can't get out of means engineering has
 * to intervene — so every path that saves the reviewer list confirms instead.
 */
export function useDroppedApprovalGate({
  persistedReviewers,
  reviewerInputs,
  setDroppedApprovalsToConfirm,
}: {
  persistedReviewers: PersistedReviewer[];
  reviewerInputs: SubmissionReviewerInput[];
  setDroppedApprovalsToConfirm: (next: { names: string[]; proceed: () => void } | null) => void;
}) {
  return useCallback(
    (proceed: () => void) => {
      const keptUserIds = new Set(reviewerInputs.map((r) => r.userId));
      const names = persistedReviewers
        .filter((r) => r.reviewerStatus === ReviewerStatus.Approved && !keptUserIds.has(r.user.id))
        .map((r) => (r.user.person ? getFullName(r.user.person) : r.user.email));

      if (names.length === 0) {
        proceed();
        return;
      }
      setDroppedApprovalsToConfirm({ names, proceed });
    },
    [persistedReviewers, reviewerInputs, setDroppedApprovalsToConfirm]
  );
}
